import { useEffect, useMemo, useState } from 'react'
import {
	Box,
	Chip,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import CommonModal from '../atoms/modal/CommonModal'
import ButtonCustom from '../atoms/button/ButtonCustom'
import StatusBtn from '../atoms/status/StatusBtn'
import { parseGuestPasteText } from '../../util/parseGuestPasteText.js'
import {
	pasteGuestListConfirm,
	pasteGuestListPreview,
} from '../../features/guestListSlice.js'
import formStyles from '../popups/UpdateKazooGuestPopup.module.scss'
import styles from './GuestImportModal.module.scss'

const PASTE_PLACEHOLDER = `First Name	Last Name	Email	Phone	Role	Guest Tags
Sam	Nguyen	sam@example.com		Guest	Family
Jordan	Lee	jordan@example.com		Guest	`

const COLUMN_CHIPS = [
	'First Name',
	'Last Name',
	'Email',
	'Phone',
	'Role',
	'Guest Tags',
]

function rowDisplayName(row = {}) {
	const name = [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
	return name || row.email || '—'
}

function buildPreviewRows(preview, sourceRows = []) {
	const byIndex = new Map()

	for (const item of preview?.valid || []) {
		byIndex.set(item.index, {
			index: item.index,
			kind: 'valid',
			row: item.row || {},
			duplicateCandidates: item.duplicateCandidates || [],
			resolvedUserId: item.resolvedUserId || null,
			errors: [],
		})
	}
	for (const item of preview?.invalid || []) {
		byIndex.set(item.index, {
			index: item.index,
			kind: 'invalid',
			row: sourceRows[item.index] || item.row || {},
			duplicateCandidates: [],
			resolvedUserId: null,
			errors: item.errors || [],
		})
	}

	return Array.from(byIndex.values()).sort((a, b) => a.index - b.index)
}

export default function GuestImportModal({ open, onClose, kazooId, onImported }) {
	const dispatch = useDispatch()
	const importStatus = useSelector((s) => s.guestList.importStatus)

	const [step, setStep] = useState('paste') // paste | preview
	const [pasteText, setPasteText] = useState('')
	const [parseError, setParseError] = useState('')
	const [parsedRows, setParsedRows] = useState([])
	const [preview, setPreview] = useState(null)
	const [resolutions, setResolutions] = useState({})

	const submitting = importStatus === 'loading'

	useEffect(() => {
		if (!open) return
		setStep('paste')
		setPasteText('')
		setParseError('')
		setParsedRows([])
		setPreview(null)
		setResolutions({})
	}, [open])

	const previewRows = useMemo(
		() => buildPreviewRows(preview, parsedRows),
		[preview, parsedRows]
	)

	const summary = useMemo(() => {
		const valid = preview?.valid?.length ?? 0
		const invalid = preview?.invalid?.length ?? 0
		const duplicates = (preview?.valid || []).filter(
			(v) => (v.duplicateCandidates || []).length > 0
		).length
		const willLink = (preview?.valid || []).filter((v) => v.resolvedUserId).length
		return { valid, invalid, duplicates, willLink }
	}, [preview])

	const confirmableCount = useMemo(() => {
		return previewRows.filter((r) => {
			if (r.kind !== 'valid') return false
			const resolution = resolutions[r.index] || 'create'
			return resolution !== 'cancel'
		}).length
	}, [previewRows, resolutions])

	const handleClose = () => {
		if (submitting) return
		onClose?.()
	}

	const handlePreview = async () => {
		setParseError('')
		const { rows, error } = parseGuestPasteText(pasteText)
		if (error) {
			setParseError(error)
			return
		}

		setParsedRows(rows)
		const result = await dispatch(pasteGuestListPreview({ kazooId, rows }))
		if (!pasteGuestListPreview.fulfilled.match(result)) return

		const nextPreview = result.payload.preview || { valid: [], invalid: [] }
		setPreview(nextPreview)

		const nextResolutions = {}
		for (const item of nextPreview.valid || []) {
			nextResolutions[item.index] = 'create'
		}
		setResolutions(nextResolutions)
		setStep('preview')
	}

	const handleConfirm = async () => {
		if (!preview || confirmableCount === 0) return

		const rowsToConfirm = (preview.valid || [])
			.map((item) => {
				const resolution = resolutions[item.index] || 'create'
				return {
					...item.row,
					duplicateResolution: resolution,
				}
			})
			.filter((row) => row.duplicateResolution !== 'cancel')

		if (rowsToConfirm.length === 0) return

		const result = await dispatch(
			pasteGuestListConfirm({ kazooId, rows: rowsToConfirm })
		)
		if (!pasteGuestListConfirm.fulfilled.match(result)) return

		onImported?.(result.payload.results)
		onClose?.()
	}

	const heading = step === 'paste' ? 'Import Guests' : 'Review Import'
	const subHeading =
		step === 'paste'
			? 'Paste rows from a spreadsheet, then preview before saving'
			: 'Invalid rows are skipped. Choose how to handle duplicates.'

	const footerContent =
		step === 'paste' ? (
			<>
				<ButtonCustom variant="outlined" size="lg" onClick={handleClose}>
					Cancel
				</ButtonCustom>
				<ButtonCustom
					variant="default"
					size="lg"
					onClick={handlePreview}
					disabled={submitting || !pasteText.trim()}
					data-cy="guest-import-preview"
				>
					{submitting ? 'Validating…' : 'Preview'}
				</ButtonCustom>
			</>
		) : (
			<>
				<ButtonCustom
					variant="outlined"
					size="lg"
					onClick={() => setStep('paste')}
					disabled={submitting}
					data-cy="guest-import-back"
				>
					Back
				</ButtonCustom>
				<ButtonCustom
					variant="default"
					size="lg"
					onClick={handleConfirm}
					disabled={submitting || confirmableCount === 0}
					data-cy="guest-import-confirm"
				>
					{submitting
						? 'Importing…'
						: `Import ${confirmableCount} guest${confirmableCount === 1 ? '' : 's'}`}
				</ButtonCustom>
			</>
		)

	return (
		<CommonModal
			open={open}
			handleClose={handleClose}
			heading={heading}
			subHeading={subHeading}
			modalInner={styles.importModalInner}
			data-cy="guest-import-modal"
			footerContent={footerContent}
		>
			{step === 'paste' ? (
				<div className={formStyles.formWrp}>
					<Box className={formStyles.formField}>
						<span className={formStyles.label}>Spreadsheet paste</span>
						<textarea
							className={styles.pasteArea}
							value={pasteText}
							onChange={(e) => setPasteText(e.target.value)}
							placeholder={PASTE_PLACEHOLDER}
							data-cy="guest-import-paste"
							spellCheck={false}
						/>
						<div className={styles.columnHelp}>
							{COLUMN_CHIPS.map((label) => (
								<Chip
									key={label}
									label={label}
									size="small"
									sx={{
										'&.MuiChip-root': {
											bgcolor: '#F2F6F8',
											borderRadius: '4px',
											height: '22px',
										},
										'& .MuiChip-label': {
											fontSize: '11px',
											fontFamily: 'Inter',
											fontWeight: 400,
											color: '#313131',
											padding: '0 6px',
										},
									}}
								/>
							))}
						</div>
						{parseError && (
							<Typography
								variant="body2"
								sx={{ color: '#D92D20', mt: 1 }}
								data-cy="guest-import-parse-error"
							>
								{parseError}
							</Typography>
						)}
					</Box>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						Copy cells from Excel or Google Sheets (tabs) or paste CSV. First Name
						and Last Name are required. Role defaults to Guest when omitted.
					</Typography>
					<div className={styles.exampleBlock} data-cy="guest-import-example">
						{`First Name\tLast Name\tEmail\tRole\tGuest Tags
Alex\tRivera\talex@example.com\tGuest\tFamily
Sam\tNguyen\tsam@example.com\tBest Man\tWedding Party`}
					</div>
				</div>
			) : (
				<div>
					<div className={styles.summaryRow} data-cy="guest-import-summary">
						<StatusBtn label={`${summary.valid} valid`} variant="active" />
						{summary.invalid > 0 && (
							<StatusBtn label={`${summary.invalid} invalid`} variant="error" />
						)}
						{summary.duplicates > 0 && (
							<StatusBtn
								label={`${summary.duplicates} duplicate warning`}
								variant="pending"
							/>
						)}
						{summary.willLink > 0 && (
							<StatusBtn
								label={`${summary.willLink} will link account`}
								variant="inprogress"
							/>
						)}
						<Typography variant="body2" color="text.secondary">
							{parsedRows.length} row{parsedRows.length === 1 ? '' : 's'} pasted
						</Typography>
					</div>

					<div className={styles.previewTableWrap}>
						<table className={styles.previewTable} data-cy="guest-import-preview-table">
							<thead>
								<tr>
									<th>#</th>
									<th>Guest</th>
									<th>Status</th>
									<th>Action</th>
								</tr>
							</thead>
							<tbody>
								{previewRows.map((item) => {
									const hasDup = (item.duplicateCandidates || []).length > 0
									const name = rowDisplayName(item.row)
									const email = item.row.email || ''
									const role = item.row.roleInWedding || 'Guest'

									return (
										<tr
											key={item.index}
											data-cy={`guest-import-row-${item.index}`}
											data-row-kind={item.kind}
										>
											<td className={styles.muted}>{item.index + 1}</td>
											<td>
												<Box sx={{ fontWeight: 600 }}>{name}</Box>
												<Box className={styles.muted} sx={{ fontSize: 12 }}>
													{[email, role].filter(Boolean).join(' · ') || '—'}
												</Box>
												{item.kind === 'invalid' && (
													<div className={styles.errorText}>
														{(item.errors || [])
															.map((e) => `${e.field}: ${e.message}`)
															.join('; ')}
													</div>
												)}
												{hasDup && (
													<div className={styles.dupText}>
														Possible duplicate of{' '}
														{item.duplicateCandidates
															.map(
																(g) =>
																	g.preferredName ||
																	[g.firstName, g.lastName]
																		.filter(Boolean)
																		.join(' ') ||
																	g.email
															)
															.filter(Boolean)
															.join(', ')}
													</div>
												)}
												{item.resolvedUserId && (
													<div className={styles.muted} style={{ fontSize: 12, marginTop: 4 }}>
														Will link OTB account
													</div>
												)}
											</td>
											<td>
												{item.kind === 'invalid' ? (
													<StatusBtn label="Invalid" variant="error" />
												) : hasDup ? (
													<StatusBtn label="Duplicate" variant="pending" />
												) : (
													<StatusBtn label="Ready" variant="active" />
												)}
											</td>
											<td>
												{item.kind === 'invalid' ? (
													<span className={styles.muted}>Skipped</span>
												) : hasDup ? (
													<TextField
														select
														size="small"
														value={resolutions[item.index] || 'create'}
														onChange={(e) =>
															setResolutions((prev) => ({
																...prev,
																[item.index]: e.target.value,
															}))
														}
														sx={{ minWidth: 140 }}
														inputProps={{
															'data-cy': `guest-import-dup-${item.index}`,
														}}
													>
														<MenuItem value="create">Create anyway</MenuItem>
														<MenuItem value="replace">Replace existing</MenuItem>
														<MenuItem value="cancel">Skip</MenuItem>
													</TextField>
												) : (
													<span className={styles.muted}>Create</span>
												)}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>

					{summary.invalid > 0 && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
							Fix invalid rows in your spreadsheet and paste again, or continue to
							import only the valid rows.
						</Typography>
					)}
				</div>
			)}
		</CommonModal>
	)
}
